/**
 * What the catalogue knows about its names and products beyond the list of names:
 *
 * - Regional words (data/regional.json, H6): "Karfiol", "Topfen", "Faschiertes" are names of their
 *   products, but a display may want the usual name instead. Other synonyms ("Fleischtomate") stay.
 * - Kinds (data/kinds.json, H7): Spaghetti are a kind of Nudeln. Who needs Nudeln can take
 *   Spaghetti; who needs Spaghetti cannot take any Nudeln. One level only.
 */
import kindsData from "../data/kinds.json" with { type: "json" };
import regionalData from "../data/regional.json" with { type: "json" };
import { product } from "./catalog.ts";
import type { Product } from "./catalog.ts";
import { findProduct } from "./lookup.ts";
import { nameKey } from "./normalize.ts";

/** Where a regional word is said: Austria, Switzerland, or parts of Germany. */
export type Region = "AT" | "CH" | "regional";

const REGIONAL: Readonly<Record<string, Readonly<Record<string, string>>>> = regionalData.names;

/** By nameKey of the regional word: its product and region. */
const BY_KEY = new Map<string, { product: Product; region: Region }>();
for (const [id, names] of Object.entries(REGIONAL)) {
  const p = product(id);
  if (!p) continue;
  for (const [name, region] of Object.entries(names)) BY_KEY.set(nameKey(name), { product: p, region: region as Region });
}

/** The regional words of a product with their region: { Karfiol: "AT" }. */
export const regionalNames = (id: string): Readonly<Record<string, Region>> => (REGIONAL[id] ?? {}) as Record<string, Region>;

/** Whether a name is a regional word of the catalogue, and where from ("Topfen" → AT); singular or plural. */
export const regionOf = (name: string): Region | undefined => BY_KEY.get(nameKey(name))?.region;

/**
 * The name to show: a regional word becomes the usual name of its product ("Karfiol" → "Blumenkohl",
 * "Semmeln" → "Brötchen"); every other name stays as it is ("Fleischtomate", "Passata", "Xyzzy").
 */
export function displayName(name: string): string {
  return BY_KEY.get(nameKey(name))?.product.names[0] ?? name;
}

/**
 * Regional words inside a longer name, word by word: "Faschiertes gemischt" → "Hackfleisch
 * gemischt", "Zitrone und Topfen" → "Zitrone und Quark". Only whole words; parts of compounds
 * ("Paradeisersauce") stay. Punctuation and spaces are kept.
 */
export function standardWords(text: string): string {
  const whole = BY_KEY.get(nameKey(text));
  if (whole) return whole.product.names[0]!;
  return text.replace(/\p{L}+/gu, (word) => BY_KEY.get(nameKey(word))?.product.names[0] ?? word);
}

const PARENT: Readonly<Record<string, string>> = kindsData.kinds;
const CHILDREN = new Map<string, string[]>();
for (const [child, parent] of Object.entries(PARENT)) CHILDREN.set(parent, [...(CHILDREN.get(parent) ?? []), child]);

/** The product this one is a kind of: kindOf("spaghetti") is "nudeln"; undefined at the top. */
export const kindOf = (id: string): string | undefined => (Object.hasOwn(PARENT, id) ? PARENT[id] : undefined);

/** The kinds of a product: kindsOf("nudeln") is ["spaghetti", "penne", …]; [] when it has none. */
export const kindsOf = (id: string): readonly string[] => CHILDREN.get(id) ?? [];

/**
 * What can stand in for a product in a recipe: itself, then its kinds. A recipe with "Nudeln" can
 * use Spaghetti; one with "Spaghetti" only Spaghetti.
 */
export const servedBy = (id: string): string[] => [id, ...kindsOf(id)];

/** The product of a name and what may stand in for it, in one step: servedByName("Pasta") → nudeln and its kinds. */
export function servedByName(name: string): string[] {
  const p = findProduct(name);
  return p ? servedBy(p.id) : [];
}
